import config from 'config';
import { format, isEqual } from 'date-fns';
import { isUndefined, noop } from 'lodash';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/Button';
import { DayPicker } from 'components/DayPicker/DayPicker';
import { SelectInput } from 'components/Forms';
import { Modal } from 'components/Modal';
import { TimeSlotPicker } from 'components/TimeSlotPicker/TimeSlotPicker';
import { Heading, Paragraph } from 'components/Typography';
import { TimeZone, TimezoneOption, timeZoneOptions } from 'domains/timezone';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { ScheduleAvailableTimes } from 'domains/calendars/types';
import { useModal } from 'utils/hooks/useModal';

type Props = {
  availableTimes: ScheduleAvailableTimes[];
  selectedDateIndex?: number;
  expressablePhoneNumber?: string;
  onChangeDay: (value: Date, index: number) => void;
  onSubmit: (value: Date, displayTimezone: TimeZone, calendarID: number, therapistName: string) => void;
  renderLoadingComponent: ({
    setIsLoading,
    selectedDate,
  }: {
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    selectedDate: string | undefined;
  }) => React.ReactNode;
};

const ScheduleEvaluation: React.FC<Props> = ({
  availableTimes,
  selectedDateIndex,
  onChangeDay,
  onSubmit,
  renderLoadingComponent,
}) => {
  const { displayTimezone, setDisplayTimezone } = useDisplayTimezone();

  const [timeSlot, setTimeSlot] = useState<Date | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { availableTimes: currentAvailableTimes = [], day: currentSelectedDate } =
    selectedDateIndex !== undefined ? availableTimes[selectedDateIndex] : {};
  const selectedAvailableTime = timeSlot ? currentAvailableTimes.find(item => isEqual(item.time, timeSlot)) : undefined;
  const currentSelectedDateWithFormat = currentSelectedDate && format(currentSelectedDate, 'EEEE, MMMM d');

  const handleOnChange = useCallback(
    (newDayIndex: number) => {
      setTimeSlot(undefined);
      onChangeDay(availableTimes[newDayIndex].day, newDayIndex);
    },
    [availableTimes, onChangeDay],
  );

  const { t } = useTranslation();
  const { openModal: openAppointmentNotFoundModal, modalProps: appointmentNotFoundModalProps } = useModal({
    dismissable: true,
  });

  const selectInputRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (selectInputRef.current) {
      selectInputRef.current.focus();
    }
  }, []);

  const showPleaseSelectDate = selectedDateIndex === undefined && !isLoading;
  const showTimeSlotPicker = currentAvailableTimes.length > 0 && !isLoading;
  const showNoAvailableTimes = selectedDateIndex !== undefined && currentAvailableTimes.length === 0 && !isLoading;

  return (
    <>
      <Heading level="h2" className="mb-16 text-left">
        {t(`schedule.mainHeading`)}
      </Heading>
      <Heading level="h7" className="mb-64 sm-d:mb-40">
        {displayTimezone
          ? t(`schedule.subHeading`, {
              timezone: displayTimezone.label,
            })
          : t(`schedule.noTimezone.subHeading`)}
      </Heading>

      <SelectInput
        data-testid="schedule-timezone-select"
        label={t('consultation.schedule.timezone')}
        options={timeZoneOptions}
        name="displayTimezone"
        value={displayTimezone?.value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          setDisplayTimezone(timeZoneOptions.find(({ value }) => value === e.target.value) as TimezoneOption);
        }}
        clearValue={noop}
        setError={noop}
        setValue={noop}
        disabled={false}
        className="mb-64 sm-d:mb-40"
        allowEmpty
        hint={isUndefined(displayTimezone) ? t('consultation.schedule.enterTimezone') : undefined}
        ref={selectInputRef}
      />

      {displayTimezone && (
        <>
          <DayPicker
            disabledWhenNoAvailableTimes={false}
            selectedDayIndex={selectedDateIndex}
            onChange={handleOnChange}
            availableTimes={availableTimes.map(item => ({
              ...item,
              availableTimes: item.availableTimes.map(time => time.time),
            }))}
            labelKey="schedule.availableDaysLabel"
          />

          {showPleaseSelectDate ? (
            <div className="-mt-40 h-96 flex items-center justify-center flex-1 gap-8 text-black-38%">
              <span className="text-sm font-semibold">{t('schedule.pleaseSelectDate')}</span>
            </div>
          ) : null}

          {showTimeSlotPicker ? (
            <TimeSlotPicker
              onChange={setTimeSlot}
              timeSlots={currentAvailableTimes.map(time => time.time)}
              value={timeSlot}
              labelKey="schedule.availableTimesLabel"
            />
          ) : null}

          {showNoAvailableTimes ? (
            <div className="-mt-40 h-96 w-full flex items-center justify-center">
              <span className="text-black font-semibold text-sm tb-64">
                {t('schedule.confirm.noAvailableTimes', {
                  date: currentSelectedDateWithFormat,
                })}
              </span>
            </div>
          ) : null}

          {renderLoadingComponent({ setIsLoading, selectedDate: currentSelectedDateWithFormat })}

          <div className="flex sm-d:flex-col gap-16">
            <Button
              data-testid="schedule-submit-button"
              className="sm-d:w-full"
              size="lg"
              disabled={isUndefined(timeSlot)}
              onClick={() => {
                if (!selectedAvailableTime) {
                  return;
                }

                return onSubmit(
                  timeSlot as Date,
                  displayTimezone.value,
                  selectedAvailableTime.calendarID,
                  selectedAvailableTime.therapistName,
                );
              }}
            >
              {t('schedule.submit')}
            </Button>

            <Button size="lg" variant="ghost" onClick={openAppointmentNotFoundModal}>
              {t('schedule.cantFindAppointment')}
            </Button>
          </div>
        </>
      )}

      <Modal {...appointmentNotFoundModalProps}>
        <Heading level="h4" className="mb-16">
          {t('schedule.appointmentNotFound.modalHeading')}
        </Heading>
        <Paragraph>
          {t('schedule.appointmentNotFound.modalSubHeading', { supportEmail: config.get('supportEmail') })}
        </Paragraph>
        <Button size="lg" className="mt-40" as="a" href={`mailto:${config.get('supportEmail')}`}>
          {t('schedule.appointmentNotFound.confirmButton')}
        </Button>
      </Modal>
    </>
  );
};

export default ScheduleEvaluation;
